import { Box, CircularProgress, Collapse, Grid, IconButton, Slider, Stack, TableCell, TableRow, Typography } from '@mui/material';
import { Fragment, useEffect, useMemo, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import HeightIcon from '@mui/icons-material/Height';
import OpacityIcon from '@mui/icons-material/Opacity';
import ScaleIcon from '@mui/icons-material/Scale';

import { GeopostCheckbox } from 'components/geopostCheckbox/GeopostCheckbox';
import { IGridXyzBasicInfo } from 'features/seismic-3d/models/interfaces/IGridXyzBasicInfo';
import { use3DViewerStore } from 'features/seismic-3d/stores/use3DViewerStore';
import { Grid3D } from '../Grid3D';
import { GridXyz3D } from '../GridXyz3D';
import { AmplitudeDomain } from 'features/seismic-3d/models/enums/AmplitudeDomain';
import { getThreeJsYFactor } from 'features/seismic-3d/utils/TileUtils';
import { useGetGridByToken } from 'features/seismic-3d/api/useSeismic3DGridController';
import { use3DSceneStore } from 'features/seismic-3d/stores/use3DSceneStore';
import { getGridGroupName } from 'features/seismic-3d/threejs/utils/GeopostGridUtils';

export type IGridsListRowProps = {
    grid: IGridXyzBasicInfo,
    visible: boolean,
    onChangeVisibility: (val: boolean) => void,
};

export const GridsListRow = ({ grid, visible, onChangeVisibility, }: IGridsListRowProps) => {
    const {
        mainFeatureSrid,
        heightPixelFactor,
        sampleInterval,
    } = use3DViewerStore(state => ({
        mainFeatureSrid: state.mainFeatureSrid,
        heightPixelFactor: state.heightPixelFactor,
        sampleInterval: state.sampleInterval,
    }));

    const minProjectionHeight = useMemo(() => -(getThreeJsYFactor(heightPixelFactor, sampleInterval)), [heightPixelFactor, sampleInterval]);

    const [detailsSearchEnabled, setDetailsSearchEnabled] = useState(false);

    const { data: gridDetails } = useGetGridByToken(grid.Token, mainFeatureSrid, detailsSearchEnabled);

    const scene = use3DSceneStore(state => state.scene);

    const [isGridCreationLoading, setIsGridCreationLoading] = useState<boolean>(false);

    const [isCollapsed, setIsCollapsed] = useState<boolean>(false);

    const [gridOpacity, setGridOpacity] = useState<number>(1);

    const [scale, setScale] = useState(1);

    const [projectionHeight, setProjectionHeight] = useState<number | null>(null);

    const domainLabel = useMemo(() => {
        switch (grid.Domain) {
            case AmplitudeDomain.Depth:
                return 'Depth';
            case AmplitudeDomain.Time:
                return 'Time';
            case AmplitudeDomain.Others:
                return 'Other';
        }
    }, [grid]);

    const labelFormatter = (value: number) => {
        return `${value * 10}%`;
    };

    const handleOpacitySliderChange = (event: Event, value: number | number[]) => {
        //const opacity = (value as number) / 10;
        setGridOpacity(value as number);
    };

    const gridNameToShow = useMemo(() => {
        const firstWord = grid.Name.split(' ')[0];
        if (firstWord.length > 30) {
            return firstWord.slice(0, 28) + '...';
        } else {
            return grid.Name;
        }
    }, [grid]);

    useEffect(() => {
        if (grid.Domain === AmplitudeDomain.Others) {
            setProjectionHeight(0.01);
        }
    }, [grid]);

    useEffect(() => {
        if (!detailsSearchEnabled && visible) {
            setDetailsSearchEnabled(true);
        }
    }, [visible, detailsSearchEnabled]);

    useEffect(() => {
        const gridObj = scene?.getObjectByName(getGridGroupName(grid.Token));
        if (!!gridObj) {
            setScale(gridObj.scale.y);
        }
    }, []);

    return (
        <Fragment>
            <TableRow>
                <TableCell>
                    <GeopostCheckbox
                        onSelect={() => onChangeVisibility(true)}
                        onUnselect={() => onChangeVisibility(false)}
                        checked={visible}
                        loading={isGridCreationLoading}
                    />

                </TableCell>
                <TableCell>
                    <IconButton
                        size='small'
                        onClick={() => setIsCollapsed(!isCollapsed)}
                    >
                        {isCollapsed ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell>
                    <Typography title={grid.Name} fontSize={'14px'}>
                        {gridNameToShow}
                    </Typography>
                </TableCell>
                <TableCell>
                    <Typography fontSize={'14px'}>{domainLabel}</Typography>
                </TableCell>

            </TableRow>
            <TableRow>
                <TableCell colSpan={3}>
                    <Collapse in={isCollapsed}>
                        <Stack paddingRight='10px' maxWidth={'250px'} gap={2}>
                            <Stack gap={1} width='100%'>
                                <Typography variant='body1'> Opacity </Typography>
                                <Stack direction='row' gap={1}>
                                    <OpacityIcon />
                                    <Slider
                                        disabled={isGridCreationLoading || !visible}
                                        min={0}
                                        max={1}
                                        step={0.1}
                                        defaultValue={1}
                                        size='small'
                                        valueLabelFormat='auto'
                                        onChange={handleOpacitySliderChange}
                                    />
                                </Stack>
                            </Stack>
                            {
                                grid.Domain === AmplitudeDomain.Others
                                &&
                                <Stack gap={1} width='100%'>
                                    <Typography variant='body1'> Height </Typography>
                                    <Stack gap={1} direction='row'>
                                        <HeightIcon />
                                        <Slider
                                            disabled={isGridCreationLoading || !visible}
                                            min={minProjectionHeight}
                                            max={0.01}
                                            step={0.01}
                                            size='small'
                                            valueLabelFormat='auto'
                                            onChange={(event, value) => setProjectionHeight(value as number)}
                                            value={projectionHeight ?? 0.01}
                                        />
                                    </Stack>
                                </Stack>
                            }
                            {
                                grid.Domain === AmplitudeDomain.Others
                                &&
                                <Stack gap={1} width='100%'>
                                    <Typography variant='body1'>Scale</Typography>
                                    <Stack direction='row' gap={1}>
                                        <ScaleIcon />
                                        <Slider
                                            disabled={isGridCreationLoading || !visible}
                                            min={0.1}
                                            max={20}
                                            step={0.01}
                                            size='small'
                                            valueLabelFormat='auto'
                                            scale={(val) => val * 100}
                                            onChange={(event, value) => setScale(value as number)}
                                            value={scale}
                                        />
                                    </Stack>
                                </Stack>
                            }
                        </Stack>
                    </Collapse>
                </TableCell>
                <TableCell>
                </TableCell>
            </TableRow>
            {
                !!gridDetails
                &&
                <GridXyz3D
                    grid={gridDetails}
                    mainFeatureSrid={mainFeatureSrid}
                    opacity={gridOpacity}
                    visible={visible}
                    onLoadStart={() => setIsGridCreationLoading(true)}
                    onLoadEnd={() => setIsGridCreationLoading(false)}
                    projectionHeight={projectionHeight}
                    scale={scale}
                />
            }
        </Fragment>
    );
};